import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getAllProducts,
  getMetaUserRolesByProductId,
} from 'src/supportPortal/services/docSideService'
import RoleSelection from './components/RoleSelection'
import { DocsideCmsContext } from 'src/supportPortal/context/DocsideCmsContextProvider'

export default function DocSideRoles() {
  const { productPackageId, contextId } = useParams()
  const navigate = useNavigate()

  const { products, contexts, loadingContexts, errorContexts, loadContexts } =
    useContext(DocsideCmsContext)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [roles, setRoles] = useState([])

  const selectedContext = contexts?.find((c) => c.id === contextId)
  const selectedProduct = products?.find((p) => p.id === productPackageId)

  // Load product and roles on component mount
  useEffect(() => {
    loadProductAndRoles()
  }, [productPackageId])

  const loadProductAndRoles = async () => {
    setLoading(true)
    setError(null)
    try {
      // Load roles for this product
      const rolesResponse = await getMetaUserRolesByProductId(contextId)
      if (rolesResponse?.status === 200) {
        setRoles(rolesResponse.data || [])
      } else {
        setError('Failed to load roles')
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleRoleSelect = (role) => {
    navigate(
      `/support-home/doc-side/${productPackageId}/contexts/${contextId}/roles/${role.id}/content`,
    )
  }

  const handleBack = () => {
    navigate(`/support-home/doc-side/${productPackageId}/contexts`)
  }

  // Handle invalid product
  if (!loading && !loadingContexts && !selectedContext && !error) {
    return (
      <div className="d-flex flex-column h-100">
        <SupportHeader
          heading={selectedProduct?.title || 'Product'}
          subHeading="Invalid Context"
          showBackButton
        />
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3, md: 4 },
            maxWidth: '1400px',
            width: '100%',
            margin: '0 auto',
            background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
            minHeight: 'calc(100vh - 80px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="error" gutterBottom sx={{ fontWeight: 600 }}>
              Invalid Context
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The context &quot;{contextId}&quot; was not found.
            </Typography>
            <Button variant="contained" onClick={handleBack} sx={{ textTransform: 'none' }}>
              Go to Contexts
            </Button>
          </Box>
        </Box>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column h-100">
      <SupportHeader
        heading={selectedContext?.title || 'Context'}
        subHeading="Select Role"
        showBackButton
      />

      <Box
        sx={{
          flex: 1,
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: '1400px',
          width: '100%',
          margin: '0 auto',
          background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        {/* Global Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            action={
              <Button
                size="small"
                onClick={() => {
                  setError(null)
                  loadProductAndRoles()
                }}
                sx={{ textTransform: 'none' }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {(loading || loadingContexts) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && selectedContext && (
          <RoleSelection
            product={selectedContext}
            roles={roles}
            onRoleSelect={handleRoleSelect}
            loading={loading || loadingContexts}
          />
        )}
      </Box>
    </div>
  )
}
